import 'reflect-metadata'
import {action, computed, makeObservable, observable} from 'mobx'
import {inject, injectable} from 'inversify'
import {MenuDataItem} from '@/types'
import {menu as menuItems} from '@/router/menu'
import {menuGroupByPath, MenuGroupKey, getMenuGroupTitles} from '@/services/menu'
import {MenuDataItemWithProduct} from '@/stores/auth/types/MenuDataItemWithProduct'
import {IMainLayoutStore} from '@/layouts/MainLayout/IMainLayoutStore'
import {VirtualTerminalStore, VirtualTerminalStoreSymbol} from '../virtual-terminal'
import {
    filterMenuItemsByPermissions,
    filterMenuItemsByProducts
} from '@/stores/main-layout/services/utils'
import {ROUTES} from '@/router/routes'
import {ProfileStoreInterface} from '@/pages/Profile'
import {ProfileStoreSymbol} from '@/pages/Profile/Profile'
import {SelectItem} from '@/components/dumb/Select'
import {storage} from '@/services/storage'
import {IS_SIDER_COLLAPSED_KEY} from '@/constants'
import {ACCESS_TOKEN_KEY} from '@/constants/auth-constants'
import {
    DossierMerchantCompanyInfo,
    DossierMerchantInfoStoreInterface,
    DossierMerchantInfoStoreSymbol
} from '@/stores/profile/DossierMerchantInfo'
import {Profile, ProfileMerchant} from '@/stores/profile/models'
import {AllStoresDataStore} from '@/stores/store-and-terminals/stores/AllStoresDataStore'
import {AllStoresDataStoreSymbol} from '@/stores/store-and-terminals/symbols'

@injectable()
export class MainLayoutStore implements IMainLayoutStore {
    private readonly _virtualTerminalStore: VirtualTerminalStore
    private readonly _profileStore: ProfileStoreInterface
    private readonly _dossierMerchantInfoStore: DossierMerchantInfoStoreInterface
    private readonly _allStoresDataStore: AllStoresDataStore

    isCollapsed = Boolean(storage.get(IS_SIDER_COLLAPSED_KEY))
    openKeys: string[] | null = null

    constructor(
        @inject(VirtualTerminalStoreSymbol) virtualTerminalStore: VirtualTerminalStore,
        @inject(ProfileStoreSymbol) profileStore: ProfileStoreInterface,
        @inject(DossierMerchantInfoStoreSymbol)
        dossierMerchantInfoStore: DossierMerchantInfoStoreInterface,
        @inject(AllStoresDataStoreSymbol) allStoresDataStore: AllStoresDataStore
    ) {
        this._virtualTerminalStore = virtualTerminalStore
        this._profileStore = profileStore
        this._dossierMerchantInfoStore = dossierMerchantInfoStore
        this._allStoresDataStore = allStoresDataStore

        makeObservable(this, {
            isCollapsed: observable,
            openKeys: observable,
            menuItems: computed,
            merchant: computed,
            setCollapsed: action.bound,
            setOpenKeys: action.bound
        })
    }

    public get menuItems(): MenuDataItem[] {
        const filterMenu = (items: MenuDataItemWithProduct[]): MenuDataItemWithProduct[] =>
            items
                .map((item) => {
                    if (
                        item.path === ROUTES.virtualTerminal &&
                        !this._virtualTerminalStore.isVirtualTerminalAvailable
                    ) {
                        return null
                    }

                    if (
                        item.path === ROUTES.digitalReceipts &&
                        !this._allStoresDataStore.hasPosStores
                    ) {
                        return null
                    }

                    if (item.children?.length) {
                        const children = filterMenu(item.children)

                        return children.length ? {...item, children} : null
                    }

                    return item
                })
                .filter(Boolean) as MenuDataItemWithProduct[]

        const filteredMenu = filterMenu(menuItems())
        const filteredMenuItemsByPermissions = filterMenuItemsByPermissions(filteredMenu)

        //trigger rerun when products are changed
        this._dossierMerchantInfoStore.products

        const base = filterMenuItemsByProducts(filteredMenuItemsByPermissions)

        const buckets: Record<MenuGroupKey, MenuDataItem[]> = {
            merchant_service: [],
            customer_growth: [],
            setting_support: []
        }

        base.forEach((item) => {
            const group = menuGroupByPath[item.path] ?? MenuGroupKey.MerchantService
            buckets[group].push(item)
        })

        return (Object.keys(buckets) as MenuGroupKey[])
            .map((key) => ({
                key: `group:${key}`,
                type: 'group' as const,
                title: getMenuGroupTitles()[key],
                children: buckets[key]
            }))
            .filter((g) => g.children?.length)
    }

    get merchants(): SelectItem[] {
        if (storage.get(ACCESS_TOKEN_KEY)) {
            return (
                this._profileStore?.profileData?.merchants?.map((merchant) => ({
                    label: merchant.name,
                    value: merchant.merchantId
                })) || []
            )
        }

        return []
    }

    get profileData(): Partial<Profile> {
        return this._profileStore.profileData
    }

    get merchant(): ProfileMerchant {
        return this._profileStore?.merchant
    }

    get merchantCompanyInfo(): DossierMerchantCompanyInfo {
        return this._dossierMerchantInfoStore?.mainInfo
    }

    public setCollapsed = (isCollapsed: boolean) => {
        storage.set(IS_SIDER_COLLAPSED_KEY, isCollapsed ? 'true' : null)
        window.dispatchEvent(new Event('storage'))

        this.isCollapsed = isCollapsed
    }

    public setOpenKeys = (keys: string[]) => {
        this.openKeys = keys
    }
}
